/**
 * Hook for fetching reward catalog metadata (categories, variants)
 * 
 * Provides data-driven categories and variants from the backend
 * instead of hardcoded values.
 */

import { useState, useEffect } from 'react';
import { rewardService } from '@/app/services/rewardService';
import { logger } from '@/app/utils/logger';

export interface RewardMetadata {
  categories: string[];
  variants: string[];
  reward_keys: string[];
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch and manage reward catalog metadata
 */
export function useRewardMetadata(): RewardMetadata {
  const [categories, setCategories] = useState<string[]>([]);
  const [variants, setVariants] = useState<string[]>([]);
  const [rewardKeys, setRewardKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        setLoading(true);
        setError(null);
        const metadata = await rewardService.getMetadata();
        setCategories(metadata.categories);
        setVariants(metadata.variants);
        setRewardKeys(metadata.reward_keys || []);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load reward metadata';
        setError(errorMessage);
        logger.error('Failed to fetch reward metadata', { error: err });
        // Set empty arrays on error to prevent crashes
        setCategories([]);
        setVariants([]);
        setRewardKeys([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMetadata();
  }, []);

  return {
    categories,
    variants,
    reward_keys: rewardKeys,
    loading,
    error,
  };
}

/**
 * Helper to convert categories array to dropdown options
 */
export function getCategoryOptions(categories: string[]): Array<{ value: string; label: string }> {
  return [
    { value: '', label: 'All Categories' },
    ...categories.map(cat => ({
      value: cat,
      label: cat
        .split(/[\s-]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' '),
    })),
  ];
}

/**
 * Helper to convert variants array to dropdown options
 */
export function getVariantOptions(variants: string[]): Array<{ value: string; label: string }> {
  return [
    { value: '', label: 'All Variants' },
    ...variants.map(variant => ({
      value: variant,
      label: variant
        .split(/[\s-]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' '),
    })),
  ];
}

